import { useMutation } from '@apollo/client';
import { Store } from '@website//Store';
import { AdminFeature } from '@website/components/admin/AdminFeature';
import DateTimePicker from '@website/components/forms/fields/DateTimePicker';
import PriceField from '@website/components/forms/fields/PriceField';
import { SubmitButton } from '@website/components/forms/fields/SubmitButton';
import PriceFormat from '@website/components/utilities/PriceFormat';
import {
  EditGroupsTableQuery,
  UpdateGroupDocument,
  UpdateGroupInput,
} from '@website/types/graphqlOperations';
import { DateTime } from 'luxon';
import { useContext, useState } from 'react';
import { Alert, Button, ButtonGroup, Col, Form, Row } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import calculateCustomerPriceZD from './utils/calculateCustomerPrice';
import sumSpacesBooked from './utils/sumSpacesBooked';

/**
 * Group Form Fields type
 *
 * Hosts want to edit the total spaces, but the mutation requires sending spaces remaining. We therefore set the field
 * as total spaces and then convert during the mutation into the spaces remaining number
 */
export type UpdateGroupFields = Omit<UpdateGroupInput, 'spacesRemaining'> & {
  totalSpaces: number;
};

interface ComposeGroupInitialValuesProps {
  existingGroups?: Array<
    Pick<
      EditGroupsTableQuery['trip']['groups']['items'][0],
      'start' | 'end' | 'remainingSpaces' | 'hostPrice'
    >
  >;
  group?: EditGroupsTableQuery['trip']['groups']['items'][0];
  spacesBooked: number;
  trip: Pick<
    EditGroupsTableQuery['trip'],
    'id' | 'startTimezone' | 'endTimezone'
  >;
}

/**
 *  Compose initial group form values
 */
export function composeGroupInitialValues({
  existingGroups,
  group,
  spacesBooked,
  trip,
}: ComposeGroupInitialValuesProps): UpdateGroupFields {
  // The group start/end dates come in UTC from the backend, and are converted
  // into the trip start/end timezones, that the host expects them to be when
  // editing them.
  const utcToTripTZDate = (date: string, tz: string) =>
    DateTime.fromISO(date, {
      zone: tz,
    }).toISO();

  if (!group) {
    const initialValues = {
      end: null,
      hostAmountZD: null,
      private: false,
      start: null,
      totalSpaces: null,
      tripID: trip.id,
    };

    // If there are existing groups
    if (existingGroups && existingGroups[0]) {
      const lastExistingGroup = existingGroups[existingGroups.length - 1];

      // Set the start/end defaults to the last group + 1 day
      initialValues.start = DateTime.fromISO(lastExistingGroup.start.utc, {
        zone: trip.startTimezone,
      })
        .plus({ days: 1 })
        .toISO();
      initialValues.end = DateTime.fromISO(lastExistingGroup.end.utc, {
        zone: trip.endTimezone,
      })
        .plus({ days: 1 })
        .toISO();

      // Set the price and spaces to the same
      initialValues.hostAmountZD = lastExistingGroup.hostPrice.amountZD;
      // Technically it should be total spaces but this is a simple fix for now
      initialValues.totalSpaces = lastExistingGroup.remainingSpaces;
    }

    return initialValues;
  }

  return {
    end: utcToTripTZDate(group.end.utc, trip.endTimezone),
    hostAmountZD: group.hostPrice.amountZD,
    hostAmountBeforeDiscountZD: group.hostPriceBeforeDiscount?.amountZD,
    id: group.id,
    private: group.private,
    start: utcToTripTZDate(group.start.utc, trip.startTimezone),
    totalSpaces: group.remainingSpaces + spacesBooked,
    tripID: trip.id,
  };
}

interface ComposeGroupMutationInputProps {
  values: UpdateGroupFields;
  trip: Pick<
    EditGroupsTableQuery['trip'],
    'id' | 'startTimezone' | 'endTimezone'
  >;
  spacesBooked: number;
}

/**
 * Compose the mutation input
 */
export function composeGroupMutationInput({
  values,
  trip,
  spacesBooked,
}: ComposeGroupMutationInputProps): UpdateGroupInput {
  function localTripDateToUTCISODate(date: string, tz: string) {
    return DateTime.fromISO(date, { zone: tz }).toUTC().toISO();
  }

  const { totalSpaces, start, end, ...formDataWithoutSpaces } = values;

  return {
    ...formDataWithoutSpaces,
    start: localTripDateToUTCISODate(start, trip.startTimezone),
    end: localTripDateToUTCISODate(end, trip.endTimezone),
    spacesRemaining: totalSpaces - spacesBooked,
  };
}

interface AddOrEditGroupItemFormProps {
  commissions: EditGroupsTableQuery['trip']['commissionOverrides'];
  existingGroups?: EditGroupsTableQuery['trip']['groups']['items'];
  group?: EditGroupsTableQuery['trip']['groups']['items'][0];
  host: EditGroupsTableQuery['trip']['host'];
  onHide: () => void;
  position: number;
  trip: Pick<
    EditGroupsTableQuery['trip'],
    'id' | 'startTimezone' | 'endTimezone'
  >;
}

/**
 * Form for adding or removing groups
 */
export default function AddOrEditGroupItemForm({
  commissions,
  existingGroups,
  group,
  host,
  onHide,
  position,
  trip,
}: AddOrEditGroupItemFormProps) {
  const { state } = useContext(Store);
  const isAdmin = state.user.roles.includes('admin');
  const alternativeRowBackground = position % 2 === 0 ? 'bg-light' : 'bg-white';
  const spacesBooked = group ? sumSpacesBooked(group) : 0;

  const [showPriceBeforeDiscount, setShowPriceBeforeDiscount] = useState(() => {
    return !!group?.hostPriceBeforeDiscount?.amountZD;
  });

  const [updateGroup, { loading, error }] = useMutation(UpdateGroupDocument, {
    update(cache, { data }) {
      // Updating works as expected if the group is updated (as it already has a cache ID), so no changes are needed there
      if (group) return;

      const tripCacheID = cache.identify({
        __typename: 'Trip',
        id: trip.id,
      });

      const groupCacheID = cache.identify({
        __typename: 'Group',
        id: data.updateGroup.id,
      });

      // Otherwise the cache item will have been created for the new group (using the groupCacheID), but it won't have been
      // inserted into the trip (as a reference) so we need to modify the trip cache to add this in.
      cache.modify({
        id: tripCacheID,
        fields: {
          groups(cachedGroups) {
            return {
              items: [...cachedGroups.items, { __ref: groupCacheID }],
            };
          },
        },
      });
    },
  });

  const defaultValues = composeGroupInitialValues({
    trip,
    group,
    spacesBooked,
    existingGroups,
  });

  const methods = useForm<UpdateGroupFields>({
    mode: 'onBlur',
    defaultValues,
  });

  const { handleSubmit, register, formState, watch, control, setValue } =
    methods;
  const { errors, isSubmitting } = formState;

  // watch field as it changes
  const spacesRemaining = watch('totalSpaces') - spacesBooked;
  const hostAmountZD = watch('hostAmountZD', defaultValues.hostAmountZD);
  const startDate = watch('start', defaultValues.start);
  const hostAmountBeforeDiscountZD = watch('hostAmountBeforeDiscountZD');

  const guestAmountZD = calculateCustomerPriceZD(
    hostAmountZD,
    startDate,
    commissions,
  );

  const guestAmountZDBeforeDiscount = calculateCustomerPriceZD(
    hostAmountBeforeDiscountZD,
    startDate,
    commissions,
  );

  const submitGroupForm = async (values: UpdateGroupFields) => {
    console.log('values', values);
    const input = composeGroupMutationInput({
      values,
      spacesBooked,
      trip,
    });

    console.log('input', input);

    await updateGroup({
      variables: {
        input,
      },
    });

    if (!error) {
      onHide();
    }
  };

  return (
    <>
      {error && (
        <Alert variant="warning">
          <Alert.Heading> Something went wrong</Alert.Heading>
          {JSON.stringify(error)}
        </Alert>
      )}
      <Form role="form" onSubmit={handleSubmit(submitGroupForm)}>
        <Row className={`${alternativeRowBackground} border-bottom`}>
          <Col className="p-1" lg={4} md={6} xs={12}>
            <label className="fw-bold">Start Date</label>
            <DateTimePicker
              name="start"
              readOnly={spacesBooked > 0 && !isAdmin}
              control={control}
              required
              ariaLabel="Start"
            />

            <label className="fw-bold">End Date</label>
            <DateTimePicker
              name="end"
              readOnly={spacesBooked > 0 && !isAdmin}
              control={control}
              required
              ariaLabel="End"
            />
          </Col>
          <Col className="p-1" lg={2} md={6} xs={12}>
            <div>
              <Form.Group controlId="name">
                <Form.Label className="fw-bold">Total Spaces</Form.Label>
                <Form.Control
                  type="number"
                  required
                  {...register('totalSpaces', {
                    required: true,
                    min: spacesBooked ?? 0,
                    valueAsNumber: true,
                  })}
                  isInvalid={!!errors.totalSpaces}
                  style={{ maxWidth: 100 }}
                />
              </Form.Group>
              <p className="text-muted small mb-0">
                Spaces Booked: {spacesBooked}
                <br />
                Spaces Remaining: {spacesRemaining}
              </p>
            </div>
          </Col>
          <Col className="p-1" lg={3} md={6} xs={12}>
            <Form.Label htmlFor="hostAmountZD" className="fw-bold">
              Amount You Receive
            </Form.Label>
            <PriceField
              name="hostAmountZD"
              currency={host.paymentDetails?.currency}
              control={control}
            />
            <p className="text-muted small mb-0">
              Customer price:{' '}
              <PriceFormat
                amount={guestAmountZD}
                currencyIso={host.paymentDetails?.currency}
                short
              />
            </p>

            <AdminFeature>
              <Form.Switch
                name="active"
                checked={showPriceBeforeDiscount}
                onChange={() => {
                  setValue('hostAmountBeforeDiscountZD', undefined);
                  setShowPriceBeforeDiscount((prev) => !prev);
                }}
                label={
                  <span className="text-muted small">
                    Show price before discount
                  </span>
                }
              />

              {showPriceBeforeDiscount && (
                <>
                  <Form.Label
                    htmlFor="hostAmountBeforeDiscountZD"
                    className="fw-bold"
                  >
                    Before Discount
                  </Form.Label>
                  <PriceField
                    name="hostAmountBeforeDiscountZD"
                    currency={host.paymentDetails?.currency}
                    control={control}
                  />
                  {hostAmountBeforeDiscountZD && (
                    <p className="text-muted small mb-0">
                      Customer price would have been:{' '}
                      <PriceFormat
                        amount={guestAmountZDBeforeDiscount}
                        currencyIso={host.paymentDetails?.currency}
                        short
                      />
                    </p>
                  )}
                </>
              )}
            </AdminFeature>
          </Col>
          <Col className="p-1" lg={2} md={6} xs={12}>
            <Form.Label className="fw-bold" htmlFor="private">
              Private
            </Form.Label>
            <Form.Check
              id="private"
              type="switch"
              label="Private"
              {...register('private')}
            />
          </Col>
          <Col className="p-1 text-right" lg={12} md={12} xs={12}>
            <ButtonGroup>
              <SubmitButton isSubmitting={loading || isSubmitting} />
              <Button
                variant="danger"
                onClick={() => onHide()}
                className="m-0 me-1 ms-1"
              >
                Cancel
              </Button>
            </ButtonGroup>
          </Col>
        </Row>
      </Form>
    </>
  );
}
